/**
 * The ink for text drawn on a swatch (WCAG 1.4.3, #206).
 *
 * The app's own swatches are tokens and read with `--color-text-on-swatch`,
 * which tokens.css sets per theme. A mailbox can carry a colour of its own,
 * though — any `#rrggbb` the user picked — and white initials on a light pick
 * (the mock's `#17a67c`, `#d9822b`) read at under 3:1. For those, pick
 * whichever of the two ink tokens contrasts more with that colour.
 *
 * Returns a CSS value for `--avatar-ink`, or undefined to keep the token ink.
 */
const LIGHT_INK: [number, number, number] = [255, 255, 255];
// --palette-neutral-950: the value of --color-swatch-ink-dark.
const DARK_INK: [number, number, number] = [0x11, 0x15, 0x1c];

function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

export function swatchInk(color: string | undefined): string | undefined {
  const hex = /^#([0-9a-f]{6})$/i.exec(color?.trim() ?? '');
  if (!hex) return undefined;
  const n = parseInt(hex[1]!, 16);
  const rgb: [number, number, number] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return contrastRatio(rgb, LIGHT_INK) >= contrastRatio(rgb, DARK_INK)
    ? 'var(--color-swatch-ink-light)'
    : 'var(--color-swatch-ink-dark)';
}
